import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Landmark, Plus, Upload, Download, Search, RefreshCw, Loader2, Pencil, Trash2,
  BadgeCheck, AlertTriangle, CheckCircle2, XCircle, FileSpreadsheet, ShieldCheck, Link2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_FORM = {
  resource_name: "", email: "", account_holder_name: "", account_number: "", ifsc: "",
  bank_name: "", branch: "", account_type: "Savings", upi: "", pan: "", gst: "", verified: false, notes: "",
};

const FIELD_LABELS = {
  resource_name: "Resource Name", email: "Email", account_holder_name: "Account Holder",
  account_number: "Account Number", ifsc: "IFSC", bank_name: "Bank Name", branch: "Branch",
  account_type: "Account Type", upi: "UPI ID", pan: "PAN", gst: "GST", notes: "Notes",
};

export default function BankAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [verifiedFilter, setVerifiedFilter] = useState("all");
  const [issuesOnly, setIssuesOnly] = useState(false);
  const [selected, setSelected] = useState([]);

  // Add/Edit dialog
  const [editAccount, setEditAccount] = useState(null); // null | {id?} form
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [ifscLookup, setIfscLookup] = useState(null);
  const [ifscLoading, setIfscLoading] = useState(false);

  const verifyIfsc = async () => {
    setIfscLoading(true);
    setIfscLookup(null);
    try {
      const res = await axios.get(`${API}/bank-accounts/ifsc-lookup/${encodeURIComponent(form.ifsc.trim())}`);
      setIfscLookup(res.data);
      if (res.data.valid) {
        setForm((p) => ({ ...p, ifsc: res.data.ifsc, bank_name: res.data.bank_name, branch: res.data.branch || p.branch }));
        toast.success(`IFSC verified — ${res.data.bank_name}, ${res.data.branch}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "IFSC directory unreachable");
    } finally { setIfscLoading(false); }
  };

  // Import wizard
  const [importOpen, setImportOpen] = useState(false);
  const [importStep, setImportStep] = useState(1);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importConfigs, setImportConfigs] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Auto-match dialog
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchSuggestions, setMatchSuggestions] = useState([]);
  const [matchSelections, setMatchSelections] = useState({}); // account_id -> email
  const [applyingMatches, setApplyingMatches] = useState(false);

  const openAutoMatch = async () => {
    setMatchOpen(true);
    setMatchLoading(true);
    setMatchSuggestions([]);
    setMatchSelections({});
    try {
      const res = await axios.get(`${API}/bank-accounts/match-suggestions`);
      const sugg = res.data.suggestions || [];
      setMatchSuggestions(sugg);
      // Preselect the top candidate for each account when score >= 0.7
      const initial = {};
      sugg.forEach((s) => {
        const top = s.candidates && s.candidates[0];
        if (top && top.score >= 0.7 && !top.already_used) initial[s.account_id] = top.email;
      });
      setMatchSelections(initial);
    } catch (e) {
      toast.error("Failed to load match suggestions");
    } finally { setMatchLoading(false); }
  };

  const applyMatches = async () => {
    const matches = Object.entries(matchSelections)
      .filter(([, email]) => !!email)
      .map(([account_id, email]) => ({ account_id, email }));
    if (matches.length === 0) { toast.error("No matches selected"); return; }
    setApplyingMatches(true);
    try {
      const res = await axios.post(`${API}/bank-accounts/match-apply`, { matches });
      toast.success(`Linked ${res.data.updated} bank accounts to resources`);
      setMatchOpen(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally { setApplyingMatches(false); }
  };

  const autoVerifyAll = async () => {
    if (!window.confirm("Mark ALL bank accounts as verified? You can unverify individual accounts later.")) return;
    try {
      const res = await axios.post(`${API}/bank-accounts/auto-verify-all`);
      toast.success(`Verified ${res.data.updated} accounts`);
      fetchData();
    } catch (e) { toast.error("Auto-verify failed"); }
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (verifiedFilter !== "all") params.verified = verifiedFilter;
      if (issuesOnly) params.issue = "any";
      const [accRes, valRes] = await Promise.all([
        axios.get(`${API}/bank-accounts`, { params }),
        axios.get(`${API}/bank-accounts/validation`),
      ]);
      setAccounts(accRes.data.accounts || []);
      setValidation(valRes.data);
    } catch (e) { toast.error("Failed to load bank accounts"); }
    finally { setLoading(false); }
  }, [search, verifiedFilter, issuesOnly]);

  useEffect(() => {
    const t = setTimeout(fetchData, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchData, search]);

  const openAdd = () => { setForm(EMPTY_FORM); setIfscLookup(null); setEditAccount({}); };
  const openEdit = (a) => {
    setForm({ ...EMPTY_FORM, ...Object.fromEntries(Object.keys(EMPTY_FORM).map((k) => [k, a[k] ?? EMPTY_FORM[k]])) });
    setIfscLookup(null);
    setEditAccount(a);
  };

  const saveAccount = async () => {
    if (!form.resource_name.trim()) { toast.error("Resource Name is required"); return; }
    setSaving(true);
    try {
      if (editAccount?.id) {
        await axios.put(`${API}/bank-accounts/${editAccount.id}`, form);
        toast.success("Bank account updated");
      } else {
        await axios.post(`${API}/bank-accounts`, form);
        toast.success("Bank account added");
      }
      setEditAccount(null);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const deleteAccount = async (a) => {
    if (!window.confirm(`Delete bank account for ${a.resource_name}?`)) return;
    try {
      await axios.delete(`${API}/bank-accounts/${a.id}`);
      toast.success("Deleted (undo available in Audit Log)");
      fetchData();
    } catch (e) { toast.error("Delete failed"); }
  };

  const bulkVerify = async (verified) => {
    try {
      await axios.post(`${API}/bank-accounts/bulk-verify`, { ids: selected, verified });
      toast.success(`${selected.length} accounts ${verified ? "verified" : "unverified"}`);
      setSelected([]);
      fetchData();
    } catch (e) { toast.error("Bulk update failed"); }
  };

  const exportMaster = async () => {
    try {
      const res = await axios.get(`${API}/bank-accounts/export`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Bank_Accounts_Master_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Master exported");
    } catch (e) { toast.error("Export failed"); }
  };

  // ── Import flow ──
  const handleFilePicked = async (file) => {
    if (!file) return;
    setImportFile(file);
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/bank-accounts/import/preview`, fd);
      setImportPreview(res.data);
      setImportConfigs(res.data.sheets.filter((s) => s.detected).map((s) => ({
        sheet: s.sheet, header_row: s.header_row, mapping: { ...s.mapping },
        default_bank_name: s.default_bank_name || "", skip: false,
      })));
      setImportStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not read file"); }
    finally { setImporting(false); }
  };

  const commitImport = async () => {
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      fd.append("config", JSON.stringify(importConfigs));
      const res = await axios.post(`${API}/bank-accounts/import/commit`, fd);
      setImportResult(res.data);
      setImportStep(3);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    finally { setImporting(false); }
  };

  const resetImport = () => {
    setImportOpen(false);
    setTimeout(() => { setImportStep(1); setImportFile(null); setImportPreview(null); setImportConfigs([]); setImportResult(null); }, 300);
  };

  const allSelected = accounts.length > 0 && selected.length === accounts.length;

  return (
    <div className="p-4 sm:p-6" data-testid="bank-accounts-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Bank Accounts</h1>
          <p className="text-sm text-slate-500 mt-0.5">Payment details for every resource — powers BLKPAY bank exports</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
          <Button variant="outline" size="sm" onClick={openAutoMatch} data-testid="auto-match-btn"><Link2 className="w-4 h-4 mr-1" />Auto-Match Names</Button>
          <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-300" onClick={autoVerifyAll} data-testid="auto-verify-all-btn"><ShieldCheck className="w-4 h-4 mr-1" />Verify All</Button>
          <Button variant="outline" size="sm" onClick={exportMaster} data-testid="export-master-btn"><Download className="w-4 h-4 mr-1" />Export Master</Button>
          <Button variant="outline" size="sm" onClick={() => setImportOpen(true)} data-testid="import-excel-btn"><Upload className="w-4 h-4 mr-1" />Import Excel</Button>
          <Button size="sm" className="bg-slate-900 hover:bg-slate-800" onClick={openAdd} data-testid="add-account-btn"><Plus className="w-4 h-4 mr-1" />Add Account</Button>
        </div>
      </div>

      {/* Validation summary */}
      {validation && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border p-4" data-testid="stat-total-accounts">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Landmark className="w-5 h-5 text-slate-600" /></div>
              <div><p className="text-2xl font-bold text-slate-900">{validation.total}</p><p className="text-xs text-slate-500">Accounts</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4" data-testid="stat-verified-accounts">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><ShieldCheck className="w-5 h-5 text-emerald-600" /></div>
              <div><p className="text-2xl font-bold text-slate-900">{validation.verified}</p><p className="text-xs text-slate-500">Verified</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4" data-testid="stat-issue-accounts">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-amber-600" /></div>
              <div><p className="text-2xl font-bold text-slate-900">{validation.with_issues}</p><p className="text-xs text-slate-500">With Issues</p></div>
            </div>
          </div>
          <div className="bg-white rounded-xl border p-4 overflow-hidden">
            <p className="text-xs text-slate-500 mb-1.5">Issue breakdown</p>
            <div className="flex flex-wrap gap-1">
              {Object.entries(validation.issues || {}).length === 0
                ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" />All clean</Badge>
                : Object.entries(validation.issues).map(([k, v]) => (
                  <Badge key={k} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">{k}: {v}</Badge>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-xl border p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
          <Input className="pl-9" placeholder="Search name, email, account, IFSC, bank…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="bank-search-input" />
        </div>
        <Select value={verifiedFilter} onValueChange={setVerifiedFilter}>
          <SelectTrigger className="w-36" data-testid="verified-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="yes">Verified</SelectItem>
            <SelectItem value="no">Unverified</SelectItem>
          </SelectContent>
        </Select>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Checkbox checked={issuesOnly} onCheckedChange={(v) => setIssuesOnly(!!v)} data-testid="issues-only-checkbox" />
          Issues only
        </label>
        {selected.length > 0 && (
          <div className="flex items-center gap-2 ml-auto">
            <span className="text-sm text-slate-500">{selected.length} selected</span>
            <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" onClick={() => bulkVerify(true)} data-testid="bulk-verify-btn">
              <BadgeCheck className="w-4 h-4 mr-1" />Verify
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulkVerify(false)} data-testid="bulk-unverify-btn">Unverify</Button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border overflow-x-auto">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
        ) : accounts.length === 0 ? (
          <div className="p-12 text-center text-slate-500" data-testid="bank-accounts-empty">
            <Landmark className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No bank accounts yet. Import your Team Accounts Excel or add manually.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"><Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? accounts.map((a) => a.id) : [])} data-testid="select-all-checkbox" /></TableHead>
                <TableHead>Resource</TableHead>
                <TableHead>Account Holder</TableHead>
                <TableHead>Account Number</TableHead>
                <TableHead>IFSC</TableHead>
                <TableHead>Bank</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((a) => (
                <TableRow key={a.id} data-testid={`bank-row-${a.id}`}>
                  <TableCell><Checkbox checked={selected.includes(a.id)} onCheckedChange={(v) => setSelected((p) => v ? [...p, a.id] : p.filter((x) => x !== a.id))} /></TableCell>
                  <TableCell>
                    <p className="font-medium text-sm">{a.resource_name}</p>
                    <p className="text-xs text-slate-500">{a.email || a.mapped_resource_email || "—"}</p>
                  </TableCell>
                  <TableCell className="text-sm">{a.account_holder_name || "—"}</TableCell>
                  <TableCell className="font-mono text-xs">{a.account_number || <span className="text-rose-500">missing</span>}</TableCell>
                  <TableCell className="font-mono text-xs">{a.ifsc || <span className="text-rose-500">missing</span>}</TableCell>
                  <TableCell className="text-xs">{a.bank_name || "—"}</TableCell>
                  <TableCell className="text-xs">{a.branch || "—"}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {a.verified
                        ? <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs"><BadgeCheck className="w-3 h-3 mr-0.5" />Verified</Badge>
                        : <Badge variant="outline" className="text-xs text-slate-500">Unverified</Badge>}
                      {(a.issues || []).map((iss) => (
                        <Badge key={iss} variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">{iss}</Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(a)} data-testid={`edit-account-${a.id}`}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-rose-500" onClick={() => deleteAccount(a)} data-testid={`delete-account-${a.id}`}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* ── Add/Edit Dialog ── */}
      <Dialog open={!!editAccount} onOpenChange={(o) => !o && setEditAccount(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="account-form-dialog">
          <DialogHeader>
            <DialogTitle>{editAccount?.id ? "Edit Bank Account" : "Add Bank Account"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["resource_name", "email", "account_holder_name", "account_number"].map((f) => (
              <div key={f}>
                <Label className="text-xs">{FIELD_LABELS[f]}{f === "resource_name" && " *"}</Label>
                <Input value={form[f] || ""} onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))} data-testid={`form-${f}`} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs">IFSC</Label>
              <div className="flex gap-2">
                <Input className="max-w-48 font-mono uppercase" value={form.ifsc || ""}
                  onChange={(e) => { setForm((p) => ({ ...p, ifsc: e.target.value.toUpperCase() })); setIfscLookup(null); }}
                  data-testid="form-ifsc" />
                <Button type="button" variant="outline" disabled={ifscLoading || !(form.ifsc || "").trim()}
                  onClick={verifyIfsc} data-testid="verify-ifsc-btn">
                  {ifscLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-1 text-emerald-600" />}
                  Verify with bank
                </Button>
              </div>
              {ifscLookup?.valid && (
                <div className="mt-1.5 text-xs bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5 text-emerald-800 flex items-start gap-1.5" data-testid="ifsc-lookup-result">
                  <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span><b>{ifscLookup.bank_name}</b> — {ifscLookup.branch}, {ifscLookup.city}, {ifscLookup.state}. Bank name & branch auto-filled.</span>
                </div>
              )}
              {ifscLookup && !ifscLookup.valid && (
                <div className="mt-1.5 text-xs bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5 text-rose-700 flex items-start gap-1.5" data-testid="ifsc-lookup-error">
                  <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{ifscLookup.error}
                </div>
              )}
            </div>
            {["bank_name", "branch"].map((f) => (
              <div key={f}>
                <Label className="text-xs">{FIELD_LABELS[f]}</Label>
                <Input value={form[f] || ""} onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))} data-testid={`form-${f}`} />
              </div>
            ))}
            <div>
              <Label className="text-xs">Account Type</Label>
              <Select value={form.account_type || "Savings"} onValueChange={(v) => setForm((p) => ({ ...p, account_type: v }))}>
                <SelectTrigger data-testid="form-account_type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Savings", "Current", "Salary"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {["upi", "pan", "gst"].map((f) => (
              <div key={f}>
                <Label className="text-xs">{FIELD_LABELS[f]} (optional)</Label>
                <Input value={form[f] || ""} onChange={(e) => setForm((p) => ({ ...p, [f]: e.target.value }))} data-testid={`form-${f}`} />
              </div>
            ))}
            <div className="sm:col-span-2">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} data-testid="form-notes" />
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Checkbox checked={!!form.verified} onCheckedChange={(v) => setForm((p) => ({ ...p, verified: !!v }))} data-testid="form-verified" />
              Mark as verified
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditAccount(null)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" disabled={saving} onClick={saveAccount} data-testid="save-account-btn">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Import Wizard ── */}
      <Dialog open={importOpen} onOpenChange={(o) => !o && resetImport()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileSpreadsheet className="w-5 h-5 text-emerald-600" />Import Bank Accounts</DialogTitle>
            <DialogDescription>
              {importStep === 1 && "Upload your Team Accounts Excel — columns are auto-detected per sheet."}
              {importStep === 2 && "Review the detected column mapping for each sheet, then import."}
              {importStep === 3 && "Import complete."}
            </DialogDescription>
          </DialogHeader>

          {importStep === 1 && (
            <div className="border-2 border-dashed rounded-xl p-10 text-center cursor-pointer hover:border-slate-400 transition-colors"
              onClick={() => fileInputRef.current?.click()} data-testid="import-dropzone">
              <input ref={fileInputRef} type="file" accept=".xlsx,.xls" className="hidden"
                onChange={(e) => handleFilePicked(e.target.files?.[0])} data-testid="import-file-input" />
              {importing ? <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" /> : (
                <>
                  <Upload className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  <p className="font-medium text-slate-700">Click to choose .xlsx file</p>
                  <p className="text-xs text-slate-500 mt-1">e.g. Team Accounts - Graphikpro.xlsx (multi-sheet supported)</p>
                </>
              )}
            </div>
          )}

          {importStep === 2 && importPreview && (
            <div className="space-y-4">
              {importConfigs.map((cfg, ci) => {
                const sheet = importPreview.sheets.find((s) => s.sheet === cfg.sheet);
                return (
                  <div key={cfg.sheet} className={`border rounded-xl p-4 ${cfg.skip ? "opacity-50" : ""}`} data-testid={`import-sheet-${cfg.sheet}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                      <div className="flex items-center gap-2">
                        <Badge>{cfg.sheet}</Badge>
                        <span className="text-sm text-slate-500">{sheet.total_rows} rows · header row {cfg.header_row}</span>
                      </div>
                      <label className="flex items-center gap-2 text-sm">
                        <Checkbox checked={cfg.skip} onCheckedChange={(v) => setImportConfigs((p) => p.map((c, i) => i === ci ? { ...c, skip: !!v } : c))} />
                        Skip this sheet
                      </label>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                      {Object.entries(sheet.headers || {}).map(([col, header]) => (
                        <div key={col}>
                          <Label className="text-xs text-slate-500 truncate block">Col {col}: {header}</Label>
                          <Select value={cfg.mapping[col] || "ignore"}
                            onValueChange={(v) => setImportConfigs((p) => p.map((c, i) => {
                              if (i !== ci) return c;
                              const m = { ...c.mapping };
                              if (v === "ignore") delete m[col]; else m[col] = v;
                              return { ...c, mapping: m };
                            }))}>
                            <SelectTrigger className="h-8 text-xs" data-testid={`mapping-${cfg.sheet}-${col}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ignore">— Ignore —</SelectItem>
                              {(importPreview.fields || []).map((f) => <SelectItem key={f} value={f}>{FIELD_LABELS[f] || f}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Default Bank Name (when column missing):</Label>
                      <Input className="h-8 max-w-56 text-xs" value={cfg.default_bank_name}
                        onChange={(e) => setImportConfigs((p) => p.map((c, i) => i === ci ? { ...c, default_bank_name: e.target.value } : c))}
                        data-testid={`default-bank-${cfg.sheet}`} />
                    </div>
                    {sheet.sample_rows?.length > 0 && (
                      <div className="mt-3 overflow-x-auto border rounded-lg">
                        <table className="text-xs w-full">
                          <tbody>
                            {sheet.sample_rows.slice(0, 4).map((r, ri) => (
                              <tr key={ri} className="border-b last:border-0">
                                {Object.keys(sheet.headers || {}).map((col) => (
                                  <td key={col} className="px-2 py-1 text-slate-600 whitespace-nowrap">{r[col] || ""}</td>
                                ))}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
              <DialogFooter>
                <Button variant="outline" onClick={() => setImportStep(1)}>Back</Button>
                <Button className="bg-emerald-600 hover:bg-emerald-700" disabled={importing || importConfigs.every((c) => c.skip)} onClick={commitImport} data-testid="commit-import-btn">
                  {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />}Import Accounts
                </Button>
              </DialogFooter>
            </div>
          )}

          {importStep === 3 && importResult && (
            <div className="space-y-4" data-testid="import-results">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3"><p className="text-2xl font-bold text-emerald-700">{importResult.imported}</p><p className="text-xs text-emerald-600">Imported</p></div>
                <div className="bg-sky-50 border border-sky-200 rounded-lg p-3"><p className="text-2xl font-bold text-sky-700">{importResult.updated}</p><p className="text-xs text-sky-600">Updated</p></div>
                <div className="bg-slate-50 border rounded-lg p-3"><p className="text-2xl font-bold text-slate-700">{importResult.skipped}</p><p className="text-xs text-slate-500">Skipped</p></div>
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3"><p className="text-2xl font-bold text-rose-700">{importResult.errors?.length || 0}</p><p className="text-xs text-rose-600">Errors</p></div>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-700 max-h-40 overflow-y-auto">
                  {importResult.errors.map((e, i) => <p key={i} className="flex items-start gap-1"><XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{e}</p>)}
                </div>
              )}
              <DialogFooter>
                <Button className="bg-slate-900 hover:bg-slate-800" onClick={resetImport} data-testid="import-done-btn">Done</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Auto-Match Names Dialog ── */}
      <Dialog open={matchOpen} onOpenChange={setMatchOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="auto-match-dialog">
          <DialogHeader>
            <DialogTitle>Auto-Match Bank Accounts to Resources</DialogTitle>
            <DialogDescription>
              Bank accounts without a linked resource email are matched by name against your resource directory.
              Review, tweak, and confirm before applying — linking by email makes BLKPAY exports work for every invoice.
            </DialogDescription>
          </DialogHeader>

          {matchLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>
          ) : matchSuggestions.length === 0 ? (
            <div className="text-center py-10 text-slate-500" data-testid="no-suggestions">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
              Every bank account is already linked to a resource. Nothing to match.
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span>{matchSuggestions.length} unlinked accounts · {Object.values(matchSelections).filter(Boolean).length} matches selected</span>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => {
                    const all = {};
                    matchSuggestions.forEach((s) => { const top = s.candidates[0]; if (top && !top.already_used) all[s.account_id] = top.email; });
                    setMatchSelections(all);
                  }} data-testid="match-select-all-top">Select best for all</Button>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setMatchSelections({})} data-testid="match-clear-all">Clear</Button>
                </div>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bank Account</TableHead>
                      <TableHead>Suggested Resource Match</TableHead>
                      <TableHead className="w-24 text-right">Confidence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {matchSuggestions.map((s) => {
                      const selected = matchSelections[s.account_id];
                      const topScore = s.candidates[0]?.score || 0;
                      return (
                        <TableRow key={s.account_id} data-testid={`match-row-${s.account_id}`}>
                          <TableCell>
                            <p className="font-medium text-sm">{s.resource_name}</p>
                            <p className="text-xs text-slate-500">
                              {s.bank_name || "—"}
                              {s.account_number_last4 && <> · ••{s.account_number_last4}</>}
                            </p>
                          </TableCell>
                          <TableCell>
                            {s.candidates.length === 0 ? (
                              <span className="text-xs text-rose-500">No candidates ≥ 0.5 — link manually</span>
                            ) : (
                              <Select value={selected || "__skip__"}
                                onValueChange={(v) => setMatchSelections((p) => {
                                  const n = { ...p };
                                  if (v === "__skip__") delete n[s.account_id]; else n[s.account_id] = v;
                                  return n;
                                })}>
                                <SelectTrigger className="h-8 text-xs" data-testid={`match-select-${s.account_id}`}><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="__skip__">— Skip (do not link) —</SelectItem>
                                  {s.candidates.map((c) => (
                                    <SelectItem key={c.email} value={c.email}>
                                      {c.display_name || c.email}
                                      {" · "}{Math.round(c.score * 100)}%
                                      {c.already_used && " · used"}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                            {selected && (
                              <p className="text-xs text-slate-500 mt-1">→ <span className="font-mono">{selected}</span></p>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {topScore >= 0.85 ? <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">{Math.round(topScore*100)}%</Badge>
                              : topScore >= 0.7 ? <Badge className="bg-sky-100 text-sky-700 border-sky-200">{Math.round(topScore*100)}%</Badge>
                              : topScore >= 0.5 ? <Badge className="bg-amber-100 text-amber-700 border-amber-200">{Math.round(topScore*100)}%</Badge>
                              : <Badge variant="outline" className="text-slate-400">—</Badge>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setMatchOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" disabled={applyingMatches || Object.values(matchSelections).filter(Boolean).length === 0} onClick={applyMatches} data-testid="apply-matches-btn">
              {applyingMatches && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Apply {Object.values(matchSelections).filter(Boolean).length} matches
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
